import { z } from "zod";

export const agaveEvalRunKindSchema = z.enum(["chat", "creation", "mixed"]);
export type AgaveEvalRunKind = z.infer<typeof agaveEvalRunKindSchema>;

export const agaveEvalRunStatusSchema = z.string().min(1);
export type AgaveEvalRunStatus = z.infer<typeof agaveEvalRunStatusSchema>;

export const agaveEvalRunSourceSchema = z.enum([
  "pr_label",
  "pr_comparison",
  "main_merge",
  "manual",
  "scheduled_monitor",
]);
export type AgaveEvalRunSource = z.infer<typeof agaveEvalRunSourceSchema>;

export const agaveEvalRunRoleSchema = z.enum(["baseline", "candidate"]);
export type AgaveEvalRunRole = z.infer<typeof agaveEvalRunRoleSchema>;

export const agaveCreationLiveEvalStatusSchema = z.enum(["running", "complete", "failed"]);
export type AgaveCreationLiveEvalStatus = z.infer<typeof agaveCreationLiveEvalStatusSchema>;

export const agaveEvalMetricKindSchema = z.preprocess(
  (value) => (value === "llm_judge" ? "llm" : (value ?? "deterministic")),
  z.enum(["llm", "deterministic"])
);
export type AgaveEvalMetricKind = z.infer<typeof agaveEvalMetricKindSchema>;

const optionalTimestampSchema = z
  .string()
  .nullish()
  .transform((value) => (value ? new Date(value).getTime() : undefined));

const timestampSchema = z.string().transform((value) => new Date(value).getTime());
const liveTimestampSchema = z
  .union([z.string(), z.number()])
  .transform((value) => (typeof value === "number" ? value : new Date(value).getTime()));
const optionalLiveTimestampSchema = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((value) => {
    if (value == null) {
      return;
    }
    return typeof value === "number" ? value : new Date(value).getTime();
  });
const recordSchema = z.record(z.unknown());
const optionalIdentitySchema = z.union([z.string(), recordSchema]).nullish();

const agaveEvalMetricConfigSchema = recordSchema.and(
  z
    .object({
      judge_prompt: z.string().nullish(),
      judgePrompt: z.string().nullish(),
      score_rubric: z.record(z.string()).nullish(),
      scoreRubric: z.record(z.string()).nullish(),
    })
    .partial()
);

const agaveEvalMetricSchema = z
  .object({
    metric_id: z.string().nullish(),
    metricId: z.string().nullish(),
    metric_kind: z.string().nullish(),
    metricKind: z.string().nullish(),
    kind: agaveEvalMetricKindSchema,
    grader_id: z.string().nullish(),
    graderId: z.string().nullish(),
    score_type: z.string().nullish(),
    scoreType: z.string().nullish(),
    scope: z.enum(["general", "case_specific"]).nullish(),
    threshold: z.unknown().optional(),
    config: agaveEvalMetricConfigSchema.nullish(),
  })
  .passthrough();

const agaveEvalJudgeCallSchema = z.object({
  request: z.string(),
  response: z.string(),
});

const agaveEvalScoreSchema = recordSchema.and(
  z
    .object({
      metric_id: z.string().nullish(),
      metricId: z.string().nullish(),
      score_type: z.string().nullish(),
      scoreType: z.string().nullish(),
      value: z.unknown(),
      passed: z.boolean().nullable(),
      reasoning: z.string().nullish(),
      judge_calls: z.array(agaveEvalJudgeCallSchema).nullish(),
      judgeCalls: z.array(agaveEvalJudgeCallSchema).nullish(),
    })
    .partial()
);

export const agaveEvalRenderedProjectSchema = z
  .object({
    projectDataUri: z.string().nullish(),
    project_data_uri: z.string().nullish(),
    videoAssetId: z.string().nullish(),
    video_asset_id: z.string().nullish(),
    error: z.string().nullish(),
  })
  .transform((value) => ({
    projectDataUri: value.projectDataUri ?? value.project_data_uri ?? null,
    videoAssetId: value.videoAssetId ?? value.video_asset_id ?? null,
    error: value.error ?? null,
  }));
export type AgaveEvalRenderedProject = z.infer<typeof agaveEvalRenderedProjectSchema>;

export const agaveEvalCostAttributionSchema = z.object({
  version: z.literal(1),
  totalCentiCredits: z.number().int().nonnegative(),
  buckets: z.object({
    agent_reasoning: z.number().int().nonnegative(),
    video_generation: z.number().int().nonnegative(),
    other_media_generation: z.number().int().nonnegative(),
    editing: z.number().int().nonnegative(),
    rendering: z.number().int().nonnegative(),
  }),
  unmappedCentiCredits: z.record(z.string(), z.number().int().nonnegative()),
});
export type AgaveEvalCostAttribution = z.infer<typeof agaveEvalCostAttributionSchema>;

const agaveEvalTraceLocatorSchema = z.object({
  provider: z.literal("braintrust"),
  projectName: z.string(),
  rootSpanId: z.string(),
});

export const agaveEvalChatTurnSchema = z.object({
  turnIndex: z.number().int().nonnegative(),
  request: z.string().nullish(),
  outcome: z.string().nullish(),
  latencySeconds: z.number().nullish(),
  credits: z.number().nullish(),
  llmCredits: z.number().nullish(),
  plannerTotalTokens: z.number().nullish(),
  costAttribution: agaveEvalCostAttributionSchema.nullish(),
  traceLocator: agaveEvalTraceLocatorSchema.nullish(),
  assistantText: z.string().nullish(),
  error: z.string().nullish(),
});
export type AgaveEvalChatTurn = z.infer<typeof agaveEvalChatTurnSchema>;

export const agaveEvalTrialResultSchema = z
  .object({
    trialIndex: z.number(),
    traceLocator: agaveEvalTraceLocatorSchema.nullish(),
    outcome: z.string().nullable().optional(),
    passed: z.boolean().nullable().optional(),
    latencySeconds: z.number().nullable().optional(),
    durationSeconds: z.number().nullable().optional(),
    assistantText: z.string().nullable().optional(),
    assistantTexts: z.array(z.string()).default([]),
    assistant_texts: z.array(z.string()).optional(),
    toolNames: z.array(z.string()).default([]),
    scores: z.array(agaveEvalScoreSchema).default([]),
    costAttribution: agaveEvalCostAttributionSchema.nullable().optional(),
    turns: z.array(agaveEvalChatTurnSchema).optional(),
    actionCount: z.number().nullable().optional(),
    actionsPreview: z.array(recordSchema).default([]),
    planError: z.string().nullable().optional(),
    applyError: z.string().nullable().optional(),
    judgeError: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
    workflowStatus: z.string().nullable().optional(),
    mutationOperationId: z.string().nullable().optional(),
    beforeRenderedProject: agaveEvalRenderedProjectSchema.nullable().optional(),
    renderedProject: agaveEvalRenderedProjectSchema.nullable().optional(),
    videoAssetId: z.string().nullish(),
    video_asset_id: z.string().nullish(),
    projectDataUri: z.string().nullish(),
    project_data_uri: z.string().nullish(),
    pipeline: recordSchema.nullable().optional(),
    quality: recordSchema.nullable().optional(),
    chargeSummary: recordSchema.nullable().optional(),
    charge_summary: recordSchema.nullable().optional(),
    executorPair: z.record(z.string(), z.unknown()).nullish(),
    executorHandoffs: z.array(z.record(z.string(), z.unknown())).optional(),
    executorRollout: z.record(z.string(), z.unknown()).nullish(),
    timings: recordSchema.nullable().optional(),
  })
  .transform((trial) => {
    const fallbackRenderedProject = agaveEvalRenderedProjectSchema.safeParse({
      videoAssetId: trial.videoAssetId,
      video_asset_id: trial.video_asset_id,
      projectDataUri: trial.projectDataUri,
      project_data_uri: trial.project_data_uri,
    });
    const renderedProject =
      trial.renderedProject ??
      (fallbackRenderedProject.success &&
      (fallbackRenderedProject.data.videoAssetId || fallbackRenderedProject.data.projectDataUri)
        ? fallbackRenderedProject.data
        : undefined);
    let assistantTexts = trial.assistantTexts;
    if (assistantTexts.length === 0 && trial.assistant_texts) {
      assistantTexts = trial.assistant_texts;
    }
    if (assistantTexts.length === 0 && trial.assistantText) {
      assistantTexts = [trial.assistantText];
    }

    return {
      ...trial,
      renderedProject,
      assistantTexts,
      chargeSummary: trial.chargeSummary ?? trial.charge_summary ?? null,
    };
  });
export type AgaveEvalTrialResult = z.infer<typeof agaveEvalTrialResultSchema>;

export const agaveEvalInputAssetSchema = z
  .object({
    assetId: z.string().nullish(),
    asset_id: z.string().nullish(),
    source: z.string(),
    fileName: z.string().nullish(),
    file_name: z.string().nullish(),
    notes: z.string().nullish(),
  })
  .transform((asset) => ({
    assetId: asset.assetId ?? asset.asset_id ?? "",
    source: asset.source,
    fileName: asset.fileName ?? asset.file_name ?? null,
    notes: asset.notes ?? null,
  }));
export type AgaveEvalInputAsset = z.infer<typeof agaveEvalInputAssetSchema>;

export const agaveEvalCaseResultSchema = z.object({
  caseName: z.string(),
  kind: agaveEvalRunKindSchema,
  mediaKind: z.string().nullable().optional(),
  suite: z.string(),
  passedAllTrials: z.boolean(),
  goal: z.string().nullable().optional(),
  inputAssets: z.array(agaveEvalInputAssetSchema).default([]),
  input: recordSchema.default({}),
  metrics: z.array(agaveEvalMetricSchema).default([]),
  trials: z.array(agaveEvalTrialResultSchema).default([]),
});
export type AgaveEvalCaseResult = z.infer<typeof agaveEvalCaseResultSchema>;

const agaveEvalRunSummaryShape = {
  gitBranch: z.string().nullish(),
  git_branch: z.string().nullish(),
  branch: z.string().nullish(),
  branchName: z.string().nullish(),
  branch_name: z.string().nullish(),
  headRef: z.string().nullish(),
  head_ref: z.string().nullish(),
  gitRef: z.string().nullish(),
  git_ref: z.string().nullish(),
  evaluatedBy: optionalIdentitySchema,
  evaluated_by: optionalIdentitySchema,
  submittedBy: optionalIdentitySchema,
  submitted_by: optionalIdentitySchema,
  triggeredBy: optionalIdentitySchema,
  triggered_by: optionalIdentitySchema,
  initiatedBy: optionalIdentitySchema,
  initiated_by: optionalIdentitySchema,
  createdBy: optionalIdentitySchema,
  created_by: optionalIdentitySchema,
  githubActor: optionalIdentitySchema,
  github_actor: optionalIdentitySchema,

  runId: z.string(),
  displayName: z.string().nullable().optional(),
  configurationId: z.string().nullable().optional(),
  caseSetId: z.string().nullable().optional(),
  caseSetVersion: z.string().nullable().optional(),
  // Independent of the dataset/profile: the same case set can run either protocol.
  evalMode: z.string().nullable().optional(),
  comparisonKey: z.string().nullable().optional(),
  status: agaveEvalRunStatusSchema,
  kind: agaveEvalRunKindSchema,
  gitSha: z.string().nullable().optional(),
  prNumber: z.number().nullable().optional(),
  authorGithubUsername: z.string().nullable().optional(),
  runSource: agaveEvalRunSourceSchema.nullable().optional(),
  runRole: agaveEvalRunRoleSchema.nullable().optional(),
  createdAt: timestampSchema,
  startedAt: optionalTimestampSchema,
  finishedAt: optionalTimestampSchema,
  taskQueue: z.string().nullable().optional(),
  evalProfile: z.string().nullable().optional(),
  evalSuite: z.string().nullable().optional(),
  creationWorkflow: z.string().nullable().optional(),
  judgeModel: z.string().nullable().optional(),
  judgeVersion: z.string().nullable().optional(),
  rubricVersion: z.string().nullable().optional(),
  caseCount: z.number(),
  trialCount: z.number(),
  casesPassedAllTrials: z.number(),
  regressionFailures: z.array(z.string()).default([]),
  failureSummary: z.string().nullable().optional(),
  archivedAt: optionalTimestampSchema,
  archivedBy: z.string().nullable().optional(),
  braintrustUrl: z.string().nullable().optional(),
  artifactUrl: z.string().nullable().optional(),
  workflowUrl: z.string().nullable().optional(),
};

export const agaveEvalRunSummarySchema = z.object(agaveEvalRunSummaryShape).passthrough();
export type AgaveEvalRunSummary = z.infer<typeof agaveEvalRunSummarySchema>;

export const agaveEvalRunDetailSchema = z
  .object({
    ...agaveEvalRunSummaryShape,
    summaries: recordSchema.default({}),
    cases: z.array(agaveEvalCaseResultSchema).default([]),
  })
  .passthrough();
export type AgaveEvalRunDetail = z.infer<typeof agaveEvalRunDetailSchema>;

const pageInfoSchema = z.object({
  hasNextPage: z.boolean(),
  endCursor: z.string().nullable().optional(),
});

const agaveEvalComparisonSummaryShape = {
  comparisonId: z.string(),
  baselineRunId: z.string(),
  candidateRunId: z.string(),
  baselineGitSha: z.string(),
  candidateGitSha: z.string(),
  prNumber: z.number().nullable().optional(),
  baseRef: z.string().nullable().optional(),
  headRef: z.string().nullable().optional(),
  runSource: agaveEvalRunSourceSchema.nullable().optional(),
  evalProfile: z.string().nullable().optional(),
  evalSuite: z.string().nullable().optional(),
  creationWorkflow: z.string().nullable().optional(),
  createdAt: timestampSchema,
  summary: z.string().nullable().optional(),
  summaryError: z.string().nullable().optional(),
  overall: z.record(recordSchema).default({}),
  artifactUrl: z.string().nullable().optional(),
  workflowUrl: z.string().nullable().optional(),
  baselineOasisUrl: z.string().nullable().optional(),
  candidateOasisUrl: z.string().nullable().optional(),
};

export const agaveEvalComparisonSummarySchema = z.object(agaveEvalComparisonSummaryShape);
export type AgaveEvalComparisonSummary = z.infer<typeof agaveEvalComparisonSummarySchema>;

export const agaveEvalComparisonDetailSchema = z.object({
  pairwise: recordSchema.nullish(),
  ...agaveEvalComparisonSummaryShape,
  summaryModel: z.string().nullable().optional(),
  summaryUsage: recordSchema.default({}),
  summaryLatencySeconds: z.number().nullable().optional(),
  metrics: z.array(recordSchema).default([]),
  usage: z.array(recordSchema).default([]),
  cases: z.array(recordSchema).default([]),
});
export type AgaveEvalComparisonDetail = z.infer<typeof agaveEvalComparisonDetailSchema>;

export const listAgaveEvalRunsResponseSchema = z.object({
  runs: z.array(agaveEvalRunSummarySchema),
  pageInfo: pageInfoSchema.default({ hasNextPage: false, endCursor: null }),
});

const agaveEvalCostByTypeSchema = z.object({
  chargeType: z.string().min(1),
  costUsd: z.number().nonnegative(),
  isPartial: z.boolean().default(false),
});

export const agaveEvalCostDashboardResponseSchema = z.object({
  currency: z.literal("USD"),
  timezone: z.literal("UTC"),
  startDate: z.string(),
  endDate: z.string(),
  totalCostUsd: z.number().nonnegative().optional(),
  isPartial: z.boolean().default(false),
  totalsByType: z.array(agaveEvalCostByTypeSchema).default([]),
  daily: z.array(
    z.object({
      date: z.string(),
      costUsd: z.number().nonnegative(),
      isPartial: z.boolean().default(false),
      costsByType: z.array(agaveEvalCostByTypeSchema).default([]),
    })
  ),
});
export type AgaveEvalCostDashboardResponse = z.infer<typeof agaveEvalCostDashboardResponseSchema>;

export const agaveEvalConfigurationSchema = z.object({
  gitBranch: z.string().nullish(),
  git_branch: z.string().nullish(),
  branch: z.string().nullish(),
  branchName: z.string().nullish(),
  branch_name: z.string().nullish(),

  configurationId: z.string(),
  displayName: z.string(),
  description: z.string().default(""),
  archivedAt: z.string().nullable().optional(),
});
export type AgaveEvalConfiguration = z.infer<typeof agaveEvalConfigurationSchema>;

export const agaveEvalConfigurationArchiveStateSchema = z.object({
  configurationId: z.string(),
  archivedAt: z.string().nullable().optional(),
});
export type AgaveEvalConfigurationArchiveState = z.infer<
  typeof agaveEvalConfigurationArchiveStateSchema
>;

export const listAgaveEvalConfigurationsResponseSchema = z.object({
  configurations: z.array(agaveEvalConfigurationSchema).default([]),
  archiveStates: z.array(agaveEvalConfigurationArchiveStateSchema).default([]),
});

export const updateAgaveEvalConfigurationArchiveResponseSchema = z
  .object({
    configuration: agaveEvalConfigurationSchema.nullish(),
    archiveState: agaveEvalConfigurationArchiveStateSchema.optional(),
  })
  .refine((response) => response.configuration != null || response.archiveState != null, {
    message: "Archive response must include configuration or archive state",
  });

export const agaveEvalBenchmarkCoverageSchema = z.object({
  configurationId: z.string(),
  caseCount: z.number().default(0),
  completedCaseCount: z.number().default(0),
  expectedAttempts: z.number().default(0),
  terminalAttempts: z.number().default(0),
  judgedAttempts: z.number().default(0),
  failedAttempts: z.number().default(0),
  official: z.boolean().default(false),
  provisional: z.boolean().default(false),
  runIds: z.array(z.string()).default([]),
  meanQualityScore: z.number().nullable().default(null),
  allTrialsSuccessPercent: z.number().nullable().default(null),
  totalCostUsd: z.number().nullable().default(null),
  meanGenerationLatencySeconds: z.number().nullable().default(null),
});
export type AgaveEvalBenchmarkCoverage = z.infer<typeof agaveEvalBenchmarkCoverageSchema>;

export const agaveEvalBenchmarkCaseSchema = z.object({
  caseId: z.string().catch("—"),
  caseDefinitionVersion: z.string().catch("—"),
  prompt: z.string().catch(""),
  promptVersion: z.string().catch("—"),
  promptChecksum: z.string().catch(""),
  sourceAssets: z.array(recordSchema).catch([]),
  referenceAssets: z.array(recordSchema).catch([]),
  operationMode: z.string().catch("—"),
  activeDimensionIds: z.array(z.string()).catch([]),
  expectedMedia: z
    .object({
      mimeTypes: z.array(z.string()).default([]),
      width: z.number().nullable().default(null),
      height: z.number().nullable().default(null),
      minDurationSeconds: z.number().nullable().default(null),
      maxDurationSeconds: z.number().nullable().default(null),
      requireAudio: z.boolean().nullable().default(null),
    })
    .catch({
      mimeTypes: [],
      width: null,
      height: null,
      minDurationSeconds: null,
      maxDurationSeconds: null,
      requireAudio: null,
    }),
});
export type AgaveEvalBenchmarkCase = z.infer<typeof agaveEvalBenchmarkCaseSchema>;

const agaveEvalBenchmarkVersionSchema = z.object({
  caseSet: z.object({
    caseSetId: z.string(),
    caseSetVersion: z.string().catch("—"),
    displayName: z.string().catch("Agave benchmark"),
    suite: z.string().catch("—"),
    profile: z.string().catch("—"),
    cases: z.array(agaveEvalBenchmarkCaseSchema).catch([]),
    rubricVersion: z.string().catch("—"),
    judgeVersion: z.string().catch("—"),
    mediaPipelineVersion: z.string().catch("—"),
    aggregationVersion: z.string().catch("—"),
    judgeRepetitionCount: z.number().catch(0),
  }),
  rollout: z
    .object({
      versionId: z.string().catch("—"),
      status: z.string().catch("—"),
      changedCaseIds: z.array(z.string()).catch([]),
      removedCaseIds: z.array(z.string()).catch([]),
      configurationIds: z.array(z.string()).catch([]),
      runIds: z.array(z.string()).catch([]),
      failureSummary: z.string().nullable().catch(null),
      coverage: z.array(agaveEvalBenchmarkCoverageSchema).default([]),
    })
    .nullable()
    .optional(),
});
export type AgaveEvalBenchmarkVersion = z.infer<typeof agaveEvalBenchmarkVersionSchema>;

export const activeAgaveEvalBenchmarkResponseSchema = z.object({
  active: agaveEvalBenchmarkVersionSchema,
  pending: agaveEvalBenchmarkVersionSchema.nullable().optional(),
});
export type ActiveAgaveEvalBenchmarkResponse = z.infer<
  typeof activeAgaveEvalBenchmarkResponseSchema
>;

export const agaveEvalPerformanceResponseSchema = z.record(z.unknown());

// These canonical endpoints contain media, judge evidence, and trajectory payloads whose
// additive fields evolve independently of Oasis. Keep the transport schema forward-compatible;
// the read-only presentation layer narrows every value before displaying it.
export const agaveEvalRunDataResponseSchema = z.record(z.unknown());
export const agaveEvalComparisonDataResponseSchema = z.record(z.unknown());

export const listAgaveEvalComparisonsResponseSchema = z.object({
  comparisons: z.array(agaveEvalComparisonSummarySchema),
  pageInfo: pageInfoSchema.default({ hasNextPage: false, endCursor: null }),
});

export const agaveCreationLiveEvalVideoAssetSchema = z.object({
  assetId: z.string(),
  url: z.string(),
  type: z.string().nullable().optional(),
  createdAt: optionalLiveTimestampSchema,
});
export type AgaveCreationLiveEvalVideoAsset = z.infer<typeof agaveCreationLiveEvalVideoAssetSchema>;

export const agaveCreationLiveEvalScoreSchema = recordSchema.and(
  z.object({
    metricId: z.string(),
    scoreType: z.string(),
    value: z.unknown().nullable().optional(),
    passed: z.boolean().nullable().optional(),
    reasoning: z.string().nullable().optional(),
    metadata: recordSchema.default({}),
  })
);
export type AgaveCreationLiveEvalScore = z.infer<typeof agaveCreationLiveEvalScoreSchema>;

const agaveCreationLiveEvalSummaryShape = {
  liveEvalId: z.string(),
  status: agaveCreationLiveEvalStatusSchema,
  createdAt: liveTimestampSchema,
  updatedAt: optionalLiveTimestampSchema,
  workflowName: z.string().nullable().optional(),
  workflowId: z.string().nullable().optional(),
  workflowRunId: z.string().nullable().optional(),
  sourceWorkflowId: z.string().nullable().optional(),
  sourceOperationId: z.string().nullable().optional(),
  persistedEditPlanId: z.string().nullable().optional(),
  projectDataUri: z.string().nullable().optional(),
  videoAssetId: z.string().nullable().optional(),
  videoAsset: agaveCreationLiveEvalVideoAssetSchema.nullable().optional(),
  goal: z.string().nullable().optional(),
  sampleLabel: z.string().nullable().optional(),
  metrics: z.array(agaveEvalMetricSchema).default([]),
  scores: z.array(agaveCreationLiveEvalScoreSchema).default([]),
  metricIds: z.array(z.string()).default([]),
  failedMetricIds: z.array(z.string()).default([]),
  unknownMetricIds: z.array(z.string()).default([]),
  judgeModel: z.string().nullable().optional(),
  rubricVersion: z.string().nullable().optional(),
  judgeError: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
};

export const agaveCreationLiveEvalSummarySchema = z.object(agaveCreationLiveEvalSummaryShape);
export type AgaveCreationLiveEvalSummary = z.infer<typeof agaveCreationLiveEvalSummarySchema>;

export const agaveCreationLiveEvalDetailSchema = z.object({
  ...agaveCreationLiveEvalSummaryShape,
  judgeUsage: recordSchema.default({}),
});
export type AgaveCreationLiveEvalDetail = z.infer<typeof agaveCreationLiveEvalDetailSchema>;

export const listAgaveCreationLiveEvalsResponseSchema = z.object({
  liveEvals: z.array(agaveCreationLiveEvalSummarySchema),
});
